<template>
  <div class="min-h-screen bg-white">
    <!-- Hero Section -->
    <section class="bg-gradient-to-br from-blue-50 to-indigo-100 py-20">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="text-center">
          <h1 class="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
            Fonctionnalités
          </h1>
          <p class="text-xl text-gray-600 max-w-3xl mx-auto">
            Découvrez tous les outils et fonctionnalités qui font d'Anamnesis la solution de référence pour la documentation patrimoniale
          </p>
        </div>
      </div>
    </section>

    <!-- Documentation structurée -->
    <section id="documentation-structuree" class="py-20 bg-white">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 class="text-3xl font-bold text-gray-900 mb-6">Documentation structurée</h2>
            <p class="text-lg text-gray-600 mb-6">
              Créez et gérez des fiches selon des modèles standardisés pour assurer la cohérence de vos données patrimoniales. Notre système de templates garantit une documentation uniforme et complète.
            </p>
            <ul class="space-y-4">
              <li class="flex items-start">
                <CheckIcon class="w-6 h-6 text-green-500 mr-3 mt-0.5" />
                <span class="text-gray-700">Modèles de fiches personnalisables</span>
              </li>
              <li class="flex items-start">
                <CheckIcon class="w-6 h-6 text-green-500 mr-3 mt-0.5" />
                <span class="text-gray-700">Validation automatique des données</span>
              </li>
              <li class="flex items-start">
                <CheckIcon class="w-6 h-6 text-green-500 mr-3 mt-0.5" />
                <span class="text-gray-700">Export en formats standards</span>
              </li>
            </ul>
          </div>
          <div>
            <img 
              src="https://placeholderimage.eu/api/600/400?category=technology" 
              alt="Documentation structurée" 
              class="rounded-lg shadow-lg"
            />
          </div>
        </div>
      </div>
    </section>

    <!-- Gestion collaborative -->
    <section id="gestion-collaborative" class="py-20 bg-gray-50">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div class="order-2 lg:order-1">
            <img 
              src="https://placeholderimage.eu/api/600/400?category=people" 
              alt="Gestion collaborative" 
              class="rounded-lg shadow-lg"
            />
          </div>
          <div class="order-1 lg:order-2">
            <h2 class="text-3xl font-bold text-gray-900 mb-6">Gestion collaborative</h2>
            <p class="text-lg text-gray-600 mb-6">
              Travaillez en équipe avec des outils de gestion des utilisateurs, agents et lieux pour vos projets patrimoniaux. Partagez et collaborez en temps réel.
            </p>
            <ul class="space-y-4">
              <li class="flex items-start">
                <CheckIcon class="w-6 h-6 text-green-500 mr-3 mt-0.5" />
                <span class="text-gray-700">Gestion des droits d'accès</span>
              </li>
              <li class="flex items-start">
                <CheckIcon class="w-6 h-6 text-green-500 mr-3 mt-0.5" />
                <span class="text-gray-700">Collaboration en temps réel</span>
              </li>
              <li class="flex items-start">
                <CheckIcon class="w-6 h-6 text-green-500 mr-3 mt-0.5" />
                <span class="text-gray-700">Historique des modifications</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </section>

    <!-- Technologie adaptable -->
    <section id="technologie-adaptable" class="py-20 bg-white">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 class="text-3xl font-bold text-gray-900 mb-6">Technologie adaptable</h2>
            <p class="text-lg text-gray-600 mb-6">
              Interface responsive et modes clair/sombre pour une utilisation optimale sur le terrain et au bureau. Accessible depuis tous vos appareils.
            </p>
            <ul class="space-y-4">
              <li class="flex items-start">
                <CheckIcon class="w-6 h-6 text-green-500 mr-3 mt-0.5" />
                <span class="text-gray-700">Interface responsive</span>
              </li>
              <li class="flex items-start">
                <CheckIcon class="w-6 h-6 text-green-500 mr-3 mt-0.5" />
                <span class="text-gray-700">Mode hors ligne</span>
              </li>
              <li class="flex items-start">
                <CheckIcon class="w-6 h-6 text-green-500 mr-3 mt-0.5" />
                <span class="text-gray-700">Synchronisation automatique</span>
              </li>
            </ul>
          </div>
          <div>
            <img 
              src="https://placeholderimage.eu/api/600/400?category=technology" 
              alt="Technologie adaptable" 
              class="rounded-lg shadow-lg"
            />
          </div>
        </div>
      </div>
    </section>

    <!-- Gestion des lieux -->
    <section id="gestion-lieux" class="py-20 bg-gray-50">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div class="order-2 lg:order-1">
            <img 
              src="https://placeholderimage.eu/api/600/400?category=monument" 
              alt="Gestion des lieux" 
              class="rounded-lg shadow-lg"
            />
          </div>
          <div class="order-1 lg:order-2">
            <h2 class="text-3xl font-bold text-gray-900 mb-6">Gestion des lieux</h2>
            <p class="text-lg text-gray-600 mb-6">
              Répertoriez et gérez tous les lieux liés à vos activités de recherche et de documentation patrimoniale. Géolocalisation et cartographie intégrées.
            </p>
            <ul class="space-y-4">
              <li class="flex items-start">
                <CheckIcon class="w-6 h-6 text-green-500 mr-3 mt-0.5" />
                <span class="text-gray-700">Géolocalisation précise</span>
              </li>
              <li class="flex items-start">
                <CheckIcon class="w-6 h-6 text-green-500 mr-3 mt-0.5" />
                <span class="text-gray-700">Cartographie interactive</span>
              </li>
              <li class="flex items-start">
                <CheckIcon class="w-6 h-6 text-green-500 mr-3 mt-0.5" />
                <span class="text-gray-700">Base de données géographique</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </section>

    <!-- Gestion des agents -->
    <section id="gestion-agents" class="py-20 bg-white">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 class="text-3xl font-bold text-gray-900 mb-6">Gestion des agents</h2>
            <p class="text-lg text-gray-600 mb-6">
              Organisez les informations sur tous les intervenants de vos projets patrimoniaux et leurs rôles. Annuaire complet et gestion des compétences.
            </p>
            <ul class="space-y-4">
              <li class="flex items-start">
                <CheckIcon class="w-6 h-6 text-green-500 mr-3 mt-0.5" />
                <span class="text-gray-700">Annuaire des intervenants</span>
              </li>
              <li class="flex items-start">
                <CheckIcon class="w-6 h-6 text-green-500 mr-3 mt-0.5" />
                <span class="text-gray-700">Gestion des rôles et compétences</span>
              </li>
              <li class="flex items-start">
                <CheckIcon class="w-6 h-6 text-green-500 mr-3 mt-0.5" />
                <span class="text-gray-700">Historique des interventions</span>
              </li>
            </ul>
          </div>
          <div>
            <img 
              src="https://placeholderimage.eu/api/600/400?category=people" 
              alt="Gestion des agents" 
              class="rounded-lg shadow-lg"
            />
          </div>
        </div>
      </div>
    </section>

    <!-- Exploitation FAIR -->
    <section id="exploitation-fair" class="py-20 bg-gray-50">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div class="order-2 lg:order-1">
            <img 
              src="https://placeholderimage.eu/api/600/400?category=technology" 
              alt="Exploitation FAIR" 
              class="rounded-lg shadow-lg"
            />
          </div>
          <div class="order-1 lg:order-2">
            <h2 class="text-3xl font-bold text-gray-900 mb-6">Exploitation FAIR</h2>
            <p class="text-lg text-gray-600 mb-6">
              Exportez et partagez vos données selon les principes FAIR pour une réutilisation optimale. Interopérabilité et standards internationaux.
            </p>
            <ul class="space-y-4">
              <li class="flex items-start">
                <CheckIcon class="w-6 h-6 text-green-500 mr-3 mt-0.5" />
                <span class="text-gray-700">Conformité aux principes FAIR</span>
              </li>
              <li class="flex items-start">
                <CheckIcon class="w-6 h-6 text-green-500 mr-3 mt-0.5" />
                <span class="text-gray-700">Export multi-formats</span>
              </li>
              <li class="flex items-start">
                <CheckIcon class="w-6 h-6 text-green-500 mr-3 mt-0.5" />
                <span class="text-gray-700">API ouverte</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="py-20 bg-blue-600">
      <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h2 class="text-3xl font-bold text-white mb-6">
          Prêt à découvrir Anamnesis ?
        </h2>
        <p class="text-xl text-blue-100 mb-8">
          Commencez dès maintenant à documenter vos projets patrimoniaux
        </p>
        <div class="flex flex-col sm:flex-row gap-4 justify-center">
          <router-link 
            to="/form" 
            class="bg-white text-blue-600 font-semibold py-3 px-8 rounded-lg hover:bg-gray-50 transition-colors duration-300"
          >
            Créer ma première fiche
          </router-link>
          <router-link 
            to="/dashboard" 
            class="bg-blue-700 text-white font-semibold py-3 px-8 rounded-lg border-2 border-blue-500 hover:bg-blue-800 transition-colors duration-300"
          >
            Accéder aux outils
          </router-link>
        </div>
      </div>
    </section>
  </div>
  <AppFooter />
</template>

<script lang="ts">
import AppFooter from '@/components/UI/AppFooter.vue';
import { CheckIcon } from "lucide-vue-next";
import { defineComponent } from "vue";

export default defineComponent({
  name: "Fonctionnalites",
  components: {
    CheckIcon,
    AppFooter
  }
});
</script>

<style scoped>
/* Styles pour les animations */
.hover\:bg-gray-50:hover {
  background-color: #f9fafb;
}

.hover\:bg-blue-800:hover {
  background-color: #1e40af;
}
</style> 